import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ChevronDown, Search, Info, FileQuestion, ArrowUpDown } from 'lucide-react';
import { useScreenSize } from '@/hooks';
import { useToast } from '@/hooks/useToast';
import { useWagerCheck } from '@/hooks/useProfile';
import Button from '@/components/common/Button/Button';
import Input from '@/components/common/Input/Input';
import WagerModal from '@/components/wallet/WagerModal';
import coinsData from '@/utils/coins';
import socket from '@/utils/socketInstance';
import { 
  C, encode, decode, forceSatoshiFormat, 
  storage, Event, __, wait, getUID, API 
} from '@/utils';
import {
  createFiatWithdrawal,
  check2FAStatus,
  verify2FA
} from '@/services/withdraw/withdrawService';
import styles from './WithdrawTab.module.css';

/**
 * WithdrawTab Component
 * Full withdrawal functionality with crypto and fiat support
 * Uses React Query for API calls and socket for real-time updates
 */
const WithdrawTab = () => {
  const { isMobile } = useScreenSize();
  const { showSuccess, showError, showInfo } = useToast();
  
  // State
  const [selectedCoin, setSelectedCoin] = useState(storage.getKey('coin') || 'BTC');
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [amount, setAmount] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCoinDropdownOpen, setIsCoinDropdownOpen] = useState(false);
  const [isNetworkDropdownOpen, setIsNetworkDropdownOpen] = useState(false);
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawAddressError, setWithdrawAddressError] = useState('');
  const [chainName, setChainName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [credits, setCredits] = useState({});
  const [activeCoins, setActiveCoins] = useState(
    localStorage.getItem('active_coins')?.split(',').map(String) || ['1', '2', '3', '4']
  );
  
  // Fiat withdrawal fields
  const [inrWithdrawalType, setInrWithdrawalType] = useState('upi'); // 'upi' | 'bank'
  const [accountHolderName, setAccountHolderName] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [upiId, setUpiId] = useState('');
  
  // 2FA
  const [showTwoFactorInput, setShowTwoFactorInput] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  
  // Wager Modal
  const [showWagerModal, setShowWagerModal] = useState(false);
  
  // Errors
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch wager check data
  const { data: wagerData } = useWagerCheck(true);
  const isEligible = wagerData?.eligible ?? true; // Default to true if data not loaded yet

  // Fiat currencies
  const FIAT_CURRENCIES = ['INR', 'MVR', 'PKR', 'AED', 'BDT', 'NPR', 'SC'];
  const isFiatCurrency = (currency) => FIAT_CURRENCIES.includes(currency);

  // Filter coins
  const filteredCoins = coinsData
    .filter(coin => {
      if (!activeCoins.includes(coin.id.toString())) return false;
      if (coin.preffix === 'BJT' || coin.preffix === 'SC') return false;
      return coin.preffix.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      if (a.preffix === 'BTC') return -1;
      if (b.preffix === 'BTC') return 1;
      return 0;
    });

  const selectedCoinData = coinsData.find(coin => coin.preffix === selectedCoin);

  // Fetch coin details (for crypto)
  const { data: coinDetails, isLoading: isLoadingCoinDetails } = useQuery({
    queryKey: ['coinDetails', selectedCoin],
    queryFn: async () => {
      if (isFiatCurrency(selectedCoin)) return null;
      const response = await fetch(`${API}/getCoinDetails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol: selectedCoin.toUpperCase() })
      });
      return response.json();
    },
    enabled: !isFiatCurrency(selectedCoin),
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  // Set default network when coin details load
  useEffect(() => {
    if (coinDetails?.networks) {
      const networks = Object.keys(coinDetails.networks);
      if (networks.length > 0 && !selectedNetwork) {
        setSelectedNetwork(networks[0]);
      }
    }
  }, [coinDetails, selectedNetwork]);

  // Socket: Listen for credit updates
  useEffect(() => {
    const token = storage.getKey('token');
    if (!token || !socket) return;

    socket.emit(C.CREDIT, encode({ token, coin: selectedCoin }));

    const handleCredit = (data) => {
      const decoded = decode(data);
      if (decoded?.credit) {
        setCredits(decoded.credit);
        storage.setKey('credits', decoded.credit);
      }
    };

    const handleCreditUpdate = (data) => {
      const decoded = decode(data);
      if (decoded?.value && decoded?.coin) {
        setCredits(prev => ({
          ...prev,
          [__.lowerCase(decoded.coin)]: decoded.value
        }));
      }
    };

    socket.on(C.CREDIT, handleCredit);
    socket.on(C.UPDATE_CREDIT, handleCreditUpdate);

    return () => {
      socket.off(C.CREDIT, handleCredit);
      socket.off(C.UPDATE_CREDIT, handleCreditUpdate);
    };
  }, [selectedCoin]);

  // Listen for coin_changed event
  useEffect(() => {
    const handleCoinChanged = (coinName) => {
      setSelectedCoin(coinName);
      setSelectedNetwork('');
      setShowPreview(false);
      setWithdrawAddress('');
      setChainName('');
    };

    Event.on('coin_changed', handleCoinChanged);
    return () => Event.off('coin_changed', handleCoinChanged);
  }, []);

  // Reset form when coin changes
  useEffect(() => {
    setAmount('');
    setShowPreview(false);
    setWithdrawAddress('');
    setChainName('');
    setPassword('');
    setPasswordError('');
    setError('');
    setAccountHolderName('');
    setBankName('');
    setAccountNumber('');
    setIfscCode('');
    setUpiId('');
  }, [selectedCoin]);

  // Fiat withdrawal mutation
  const fiatWithdrawalMutation = useMutation({
    mutationFn: createFiatWithdrawal,
    onSuccess: (data) => {
      // The service function now ensures success: true for 201/200 status codes
      if (data?.success !== false) {
        showSuccess(data?.message || 'Withdrawal request submitted successfully');
        // Reset form
        setAmount('');
        setAccountHolderName('');
        setBankName('');
        setAccountNumber('');
        setIfscCode('');
        setUpiId('');
        setShowPreview(false);
        // Refresh credits
        const token = storage.getKey('token');
        if (token && socket) {
          socket.emit(C.CREDIT, encode({ token, coin: selectedCoin }));
        }
        setTimeout(() => window.location.reload(), 2000);
      } else {
        showError(data?.message || data?.error || 'Failed to submit withdrawal request');
      }
    },
    onError: (error) => {
      // Handle actual errors (not 201/200 status codes, as those are handled in service)
      showError(error?.response?.data?.message || error?.message || 'Failed to submit withdrawal request');
    }
  });

  // 2FA check mutation
  const check2FAMutation = useMutation({
    mutationFn: () => check2FAStatus(getUID()),
    onSuccess: (data) => {
      setIs2FAEnabled(data.isEnabled || false);
      if (data.isEnabled) {
        setShowTwoFactorInput(true);
      } else {
        handleWithdrawSubmit();
      }
    }
  });

  // 2FA verify mutation
  const verify2FAMutation = useMutation({
    mutationFn: (code) => verify2FA({ token: code }),
    onSuccess: (data) => {
      if (data.success) {
        setShowTwoFactorInput(false);
        setTwoFactorCode('');
        handleWithdrawSubmit();
      } else {
        showError('Invalid verification code');
      }
    },
    onError: () => {
      showError('Failed to verify code');
    }
  });

  // Handle coin selection
  const handleCoinSelect = (coinName) => {
    if (storage.getKey('coin') === coinName) {
      setIsCoinDropdownOpen(false);
      return;
    }

    storage.setKey('coin', coinName);
    setSelectedCoin(coinName);
    setSelectedNetwork('');
    setShowPreview(false);
    setIsCoinDropdownOpen(false);
    
    const coinKey = __.lowerCase(coinName);
    const creditVal = credits[coinKey] || 0;
    const formattedCredit = forceSatoshiFormat(creditVal, coinName);
    storage.setKey('credit', formattedCredit);
    
    wait(200).then(() => {
      Event.emit('coin_changed', coinName);
    });

    const token = storage.getKey('token');
    if (token && socket) {
      socket.emit(C.CREDIT, encode({ token, coin: coinName }));
    }
  };

  // Calculate fee and final amount
  const calculateFee = () => {
    if (!amount || parseFloat(amount) <= 0) return 0;
    if (isFiatCurrency(selectedCoin)) return 0;
    return parseFloat(amount) * 0.01; // 1% fee for crypto
  };

  const calculateFinalAmount = () => {
    if (!amount || parseFloat(amount) <= 0) return 0;
    if (isFiatCurrency(selectedCoin)) return parseFloat(amount);
    return parseFloat(amount) - calculateFee();
  };

  // Validate form
  const validateForm = () => {
    setError('');
    setWithdrawAddressError('');
    setPasswordError('');

    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return false;
    }

    const coinInfo = coinsData.find(c => c.preffix === selectedCoin);
    if (coinInfo && parseFloat(amount) < Number(coinInfo.min)) {
      setError(`Minimum withdrawal amount is ${coinInfo.min} ${selectedCoin}`);
      return false;
    }

    // MVR specific validation
    if (selectedCoin === 'MVR') {
      const amountNum = parseFloat(amount);
      if (amountNum < 500) {
        setError('Minimum withdrawal amount for MVR is 500');
        return false;
      }
      if (amountNum > 50000) {
        setError('Maximum withdrawal amount for MVR is 50,000');
        return false;
      }
    }

    if (isFiatCurrency(selectedCoin)) {
      if (!accountHolderName.trim()) {
        setError('Account holder name is required');
        return false;
      }

      if (selectedCoin === 'INR') {
        if (inrWithdrawalType === 'upi' && !upiId.trim()) {
          setError('UPI ID is required for UPI withdrawals');
          return false;
        } else if (inrWithdrawalType === 'bank' && (!bankName || !accountNumber || !ifscCode)) {
          setError('Bank details are required for bank transfers');
          return false;
        }
      } else {
        if (!bankName || !accountNumber || !ifscCode) {
          setError('Bank details are required');
          return false;
        }
      }
    } else {
      // Crypto validation
      if (!withdrawAddress.trim()) {
        setWithdrawAddressError('Withdrawal address is required');
        return false;
      }
      if (!chainName.trim()) {
        setError('Chain name is required');
        return false;
      }
    }

    return true;
  };

  // Handle withdraw submit
  const handleWithdrawSubmit = () => {
    if (!validateForm()) return;

    if (isFiatCurrency(selectedCoin)) {
      const payload = {
        amount: amount,
        currency: selectedCoin,
        withdrawal_type: selectedCoin === 'INR' ? inrWithdrawalType : 'bank',
        bank_name: bankName,
        account_number: accountNumber,
        account_holder_name: accountHolderName,
        ifsc_code: ifscCode,
        upi_id: upiId
      };
      fiatWithdrawalMutation.mutate(payload);
    } else {
      // Crypto withdrawal - use socket
      if (!password.trim()) {
        setPasswordError('Password is required');
        return;
      }

      if (!showPreview) {
        setShowPreview(true);
        return;
      }

      // Final confirmation
      setLoading(true);
      const fee = calculateFee();
      
      setTimeout(() => {
        socket.emit(
          C.SUBMIT_NEW_WITHDRAWL,
          encode({
            coin: selectedCoin,
            wallet: withdrawAddress,
            amount: amount,
            immed: fee,
            password: password,
            chain: chainName,
            network: selectedNetwork
          })
        );

        // Reset form
        setAmount('');
        setWithdrawAddress('');
        setPassword('');
        setChainName('');
        setShowPreview(false);
        setLoading(false);
        showSuccess('Withdrawal request submitted successfully');
        
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }, 500);
    }
  };

  // Handle submit with 2FA check
  const handleSubmit = async () => {
    if (!validateForm()) return;

    // Check wager eligibility first
    if (!isEligible) {
      setShowWagerModal(true);
      return;
    }

    // Check 2FA status first
    check2FAMutation.mutate();
  };

  // Handle 2FA verification
  const handle2FAVerification = () => {
    if (!twoFactorCode.trim()) {
      showError('Please enter verification code');
      return;
    }
    verify2FAMutation.mutate(twoFactorCode);
  };

  // Helper function to get coin image path
  const getCoinImagePath = (coin) => {
    if (!coin || !coin.image) return '/assets/images/coins/BTC.webp';
    const coinImageMap = {
      'USDT': 'USDT.webp',
      'TRX': 'TRON.webp',
    };
    let imageName = coinImageMap[coin.preffix] || coin.image;
    imageName = imageName.replace(/^coins\//, '').replace(/^assets\/images\//, '');
    return `/assets/images/coins/${imageName}`;
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles.dropdownContainer}`)) {
        setIsCoinDropdownOpen(false);
        setIsNetworkDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const isLoading = fiatWithdrawalMutation.isPending || 
                   check2FAMutation.isPending ||
                   verify2FAMutation.isPending ||
                   isLoadingCoinDetails ||
                   loading;

  const fee = calculateFee();
  const finalAmount = calculateFinalAmount();

  return (
    <div className={styles.withdrawTab}>
      {/* Coin Selection */}
      <div className={styles.formGroup}>
        <label className={styles.label}>Withdrawal Currency</label>
        <div className={`${styles.dropdownContainer} ${styles.dropdownContainer}`}>
          <button
            className={styles.dropdownButton}
            onClick={(e) => {
              e.stopPropagation();
              setIsCoinDropdownOpen(!isCoinDropdownOpen);
            }}
          >
            <div className={styles.dropdownButtonContent}>
              {selectedCoinData && (
                <img
                  src={getCoinImagePath(selectedCoinData)}
                  alt={selectedCoin}
                  className={styles.coinImage}
                  onError={(e) => {
                    if (selectedCoin === 'USDT' && e.target.src.includes('USDT.webp')) {
                      e.target.src = '/assets/images/coins/TETHER.webp';
                    } else {
                      e.target.src = '/assets/images/coins/BTC.webp';
                    }
                  }}
                />
              )}
              <span className={styles.coinName}>{selectedCoin}</span>
            </div>
            <ChevronDown size={20} className={styles.chevron} />
          </button>

          {isCoinDropdownOpen && (
            <div className={styles.dropdownPanel} onClick={(e) => e.stopPropagation()}>
              <div className={styles.searchContainer}>
                <Search size={16} className={styles.searchIcon} />
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search coins..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>

              <div className={styles.coinList}>
                {filteredCoins.map((coin) => (
                  <div
                    key={coin.id}
                    className={`${styles.coinOption} ${selectedCoin === coin.preffix ? styles.coinOptionSelected : ''}`}
                    onClick={() => handleCoinSelect(coin.preffix)}
                  >
                    <img
                      src={getCoinImagePath(coin)}
                      alt={coin.preffix}
                      className={styles.coinImage}
                      onError={(e) => {
                        if (coin.preffix === 'USDT' && e.target.src.includes('USDT.webp')) {
                          e.target.src = '/assets/images/coins/TETHER.webp';
                        } else {
                          e.target.src = '/assets/images/coins/BTC.webp';
                        }
                      }}
                    />
                    <span className={styles.coinName}>{coin.preffix}</span>
                    <span className={styles.coinBalance}>
                      {forceSatoshiFormat(credits[__.lowerCase(coin.preffix)] || 0)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Network Selection (for crypto) */}
      {!isFiatCurrency(selectedCoin) && coinDetails?.networks && (
        <div className={styles.formGroup}>
          <label className={styles.label}>Choose Network</label>
          <div className={`${styles.dropdownContainer} ${styles.dropdownContainer}`}>
            <button
              className={styles.dropdownButton}
              onClick={(e) => {
                e.stopPropagation();
                setIsNetworkDropdownOpen(!isNetworkDropdownOpen);
              }}
            >
              <span className={styles.coinName}>{selectedNetwork || 'Select Network'}</span>
              <ChevronDown size={20} className={styles.chevron} />
            </button>

            {isNetworkDropdownOpen && (
              <div className={styles.dropdownPanel} onClick={(e) => e.stopPropagation()}>
                {Object.keys(coinDetails.networks).map((network) => (
                  <div
                    key={network}
                    className={`${styles.coinOption} ${selectedNetwork === network ? styles.coinOptionSelected : ''}`}
                    onClick={() => {
                      setSelectedNetwork(network);
                      setIsNetworkDropdownOpen(false);
                    }}
                  >
                    <span className={styles.coinName}>{network}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Amount Input */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          Enter Withdrawal Amount
          {selectedCoin === 'MVR' && (
            <span className={styles.minAmount}> (Min: 500, Max: 50,000 MVR)</span>
          )}
        </label>
        <Input
          type="number"
          placeholder={`Enter amount in ${selectedCoin}`}
          value={amount}
          onChange={(e) => {
            const newAmount = e.target.value;
            setAmount(newAmount);
            setError('');
            
            if (selectedCoin === 'MVR') {
              const amountNum = parseFloat(newAmount);
              if (newAmount && !isNaN(amountNum)) {
                if (amountNum < 500) {
                  setError('Minimum withdrawal amount for MVR is 500');
                } else if (amountNum > 50000) {
                  setError('Maximum withdrawal amount for MVR is 50,000');
                }
              }
            }
          }}
          error={error}
        />
      </div>

      {/* Crypto Form */}
      {!isFiatCurrency(selectedCoin) && (
        <>
          <div className={styles.formGroup}>
            <label className={styles.label}>
              Withdrawal Address ({selectedCoin})
            </label>
            <Input
              type="text"
              placeholder={`Enter your ${selectedCoin} wallet address`}
              value={withdrawAddress}
              onChange={(e) => {
                setWithdrawAddress(e.target.value);
                setWithdrawAddressError('');
              }}
              error={withdrawAddressError}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Chain Name</label>
            <Input
              type="text"
              placeholder={`Enter ${selectedCoin} chain/network name`}
              value={chainName}
              onChange={(e) => {
                setChainName(e.target.value);
                setError('');
              }}
              error={error && !withdrawAddressError ? error : ''}
            />
          </div>

          {showPreview && (
            <div className={styles.previewContainer}>
              <h3 className={styles.previewTitle}>Confirm Withdrawal</h3>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>Address:</span>
                <span className={styles.previewValue}>{withdrawAddress}</span>
              </div>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>Network:</span>
                <span className={styles.previewValue}>{selectedCoin}</span>
              </div>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>Chain:</span>
                <span className={styles.previewValue}>{chainName}</span>
              </div>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>Amount:</span>
                <span className={styles.previewValue}>
                  {parseFloat(amount || 0).toFixed(8)} {selectedCoin}
                </span>
              </div>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>Fee (1%):</span>
                <span className={styles.previewValue}>
                  {fee.toFixed(8)} {selectedCoin}
                </span>
              </div>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>You will receive:</span>
                <span className={styles.previewValueGreen}>
                  {finalAmount.toFixed(8)} {selectedCoin}
                </span>
              </div>

              <div className={styles.formGroup}>
                <Input
                  type="password"
                  label="Enter Password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError('');
                  }}
                  error={passwordError}
                />
              </div>

              <div className={styles.previewActions}>
                <Button
                  variant="secondary"
                  onClick={() => setShowPreview(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleWithdrawSubmit}
                  disabled={isLoading || !password}
                >
                  {isLoading ? 'Processing...' : 'Confirm Withdrawal'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Fiat Form */}
      {isFiatCurrency(selectedCoin) && (
        <>
          {selectedCoin === 'INR' && (
            <div className={styles.paymentModeContainer}>
              <div className={styles.methodSelector}>
                <button
                  className={`${styles.methodButton} ${inrWithdrawalType === 'upi' ? styles.methodButtonActive : ''}`}
                  onClick={() => setInrWithdrawalType('upi')}
                >
                  UPI
                </button>
                <button
                  className={`${styles.methodButton} ${inrWithdrawalType === 'bank' ? styles.methodButtonActive : ''}`}
                  onClick={() => setInrWithdrawalType('bank')}
                >
                  Bank Transfer
                </button>
              </div>
            </div>
          )}

          <Input
            type="text"
            label="Account Holder Name"
            placeholder="Enter account holder name"
            value={accountHolderName}
            onChange={(e) => setAccountHolderName(e.target.value)}
          />

          {selectedCoin === 'INR' && inrWithdrawalType === 'upi' ? (
            <Input
              type="text"
              label="UPI ID"
              placeholder="Enter UPI ID (example@upi)"
              value={upiId}
              onChange={(e) => setUpiId(e.target.value)}
            />
          ) : (
            <>
              <Input
                type="text"
                label="Bank Name"
                placeholder="Enter bank name"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
              />

              <Input
                type="text"
                label="Account Number"
                placeholder="Enter account number"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
              />

              <Input
                type="text"
                label="IFSC Code"
                placeholder="Enter IFSC code"
                value={ifscCode}
                onChange={(e) => setIfscCode(e.target.value)}
              />
            </>
          )}
        </>
      )}

      {/* Withdrawal Summary */}
      {amount && parseFloat(amount) > 0 && (
        <div className={styles.summaryContainer}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Withdrawal Amount</span>
            <span className={styles.summaryValue}>
              {parseFloat(amount).toFixed(isFiatCurrency(selectedCoin) ? 2 : 8)} {selectedCoin}
            </span>
          </div>
          {!isFiatCurrency(selectedCoin) && (
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>
                Fee (1%):
                <FileQuestion size={14} className={styles.infoIcon} />
              </span>
              <span className={styles.summaryValue}>
                {fee.toFixed(8)} {selectedCoin}
              </span>
            </div>
          )}
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Total Withdrawal Amount</span>
            <span className={styles.summaryValue}>
              {finalAmount.toFixed(isFiatCurrency(selectedCoin) ? 2 : 8)} {selectedCoin}
            </span>
          </div>
        </div>
      )}

      {/* Action Button */}
      {!showPreview && (
        <Button
          variant="primary"
          fullWidth
          onClick={handleSubmit}
          disabled={isLoading || !amount || parseFloat(amount) <= 0}
        >
          {isLoading ? 'Processing...' : isFiatCurrency(selectedCoin) ? 'Withdraw' : 'Preview'}
        </Button>
      )}

      {/* MVR Note */}
      {selectedCoin === 'MVR' && (
        <div className={styles.noteContainer}>
          <Info size={20} className={styles.noteIcon} />
          <p className={styles.noteText}>
            Note: Total 2 withdrawals per day. Unlimited withdrawal players can swap MVR to Crypto.
          </p>
        </div>
      )}

      {/* Warning */}
      <div className={styles.warningContainer}>
        <Info size={20} className={styles.warningIcon} />
        <p className={styles.warningText}>
          {isFiatCurrency(selectedCoin)
            ? 'For security purposes, large or suspicious withdrawals may take 1-6 hours for audit process. We appreciate your patience!'
            : `Please ensure you're using the correct ${selectedNetwork} network for your ${selectedCoin} withdrawal. Sending to an incorrect network may result in permanent loss of funds.`
          }
        </p>
      </div>

      {/* 2FA Modal */}
      {showTwoFactorInput && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Two-Factor Authentication</h3>
            <Input
              type="text"
              label="Enter your 2FA verification code"
              placeholder="Enter verification code"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value)}
            />
            <div className={styles.modalActions}>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowTwoFactorInput(false);
                  setTwoFactorCode('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handle2FAVerification}
                disabled={verify2FAMutation.isPending || !twoFactorCode}
              >
                {verify2FAMutation.isPending ? 'Verifying...' : 'Verify'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Wager Modal */}
      <WagerModal
        isOpen={showWagerModal}
        onClose={() => setShowWagerModal(false)}
      />
    </div>
  );
};

export default WithdrawTab;
